import React from 'react'
import { Pressable, Text, View } from 'react-native'
import styled from 'styled-components/native'
import TextInput from '../../../../components/common/TextInput'
import useTranslation from '../../../../hooks/useTranslation'
import { ImageTextBlockProps } from '../../../../doclets/types'
import { BlockSettingsProps, HelpText, SettingLabel } from '../settingsStyles'
import { builderColors, builderRadii } from '../builderColors'

interface Choice<T extends string> {
  value: T
  label: string
}

// Two or three options side by side, the chosen one filled
const ChoiceRow = <T extends string>({
  label,
  choices,
  value,
  onChoose
}: {
  label: string
  choices: Choice<T>[]
  value: T
  onChoose: (value: T) => void
}) => (
  <View style={{ flexDirection: 'row' }} accessibilityRole="radiogroup">
    {choices.map((choice, i) => {
      const chosen = choice.value === value
      return (
        <Pressable
          key={choice.value}
          accessibilityRole="radio"
          accessibilityState={{ checked: chosen }}
          accessibilityLabel={`${label}: ${choice.label}`}
          onPress={() => onChoose(choice.value)}
          style={{
            flex: 1,
            alignItems: 'center',
            paddingVertical: 6,
            marginLeft: i ? 6 : 0,
            borderWidth: 1,
            borderRadius: builderRadii.md,
            borderColor: chosen
              ? builderColors.selectedBorder
              : builderColors.border,
            backgroundColor: chosen ? builderColors.selectedBorder : '#FFFFFF'
          }}
        >
          <ChoiceText
            style={{ color: chosen ? '#FFFFFF' : builderColors.text }}
          >
            {choice.label}
          </ChoiceText>
        </Pressable>
      )
    })}
  </View>
)

// Image + text settings in the sidebar. The text is edited on the canvas.
const ImageTextBlockSettings = ({
  block,
  onChange
}: BlockSettingsProps<'imageText'>) => {
  const { t } = useTranslation()
  const { side, imageSize, alt } = block.props
  const set = (values: Partial<ImageTextBlockProps>) =>
    onChange({ ...block.props, ...values })

  return (
    <>
      <Field>
        <SettingLabel>{t('doclets:imageText:side')}</SettingLabel>
        <ChoiceRow
          label={t('doclets:imageText:side')}
          choices={[
            { value: 'left', label: t('doclets:imageText:left') },
            { value: 'right', label: t('doclets:imageText:right') }
          ]}
          value={side}
          onChoose={value => set({ side: value })}
        />
      </Field>
      <Field>
        <SettingLabel>{t('doclets:imageText:size')}</SettingLabel>
        <ChoiceRow
          label={t('doclets:imageText:size')}
          choices={[
            { value: 'standard', label: t('doclets:imageText:standard') },
            { value: 'small', label: t('doclets:imageText:small') }
          ]}
          value={imageSize}
          onChoose={value => set({ imageSize: value })}
        />
        <HelpText style={{ marginTop: 6 }}>
          {t('doclets:imageText:sizeHelp')}
        </HelpText>
      </Field>
      <Field>
        <SettingLabel>{t('doclets:image:alt')}</SettingLabel>
        <TextInput
          placeholder=""
          value={alt}
          onChangeText={value => set({ alt: value })}
        />
        <HelpText style={{ marginTop: 6 }}>
          {t('doclets:image:altHelp')}
        </HelpText>
      </Field>
    </>
  )
}

const ChoiceText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
`

const Field = styled(View)`
  padding: 12px 0;
  border-top-width: 1px;
  border-top-color: ${builderColors.border};
`

export default ImageTextBlockSettings
